/**
 * RatingSummary Component
 *
 * Displays aggregate rating data (average rating and vote count) for photographers.
 *
 * Feature: 649-critique-feedback-options (T037)
 */

import { Star } from '@/components/atoms/Star/Star';
import { Text } from '@/components/atoms/Text/Text';
import { useRatingSummaryForPhoto } from '@/graphql/hooks/usePhotoRatingsGraphQL';
import { cn } from '@/lib/utils';

export interface RatingSummaryProps {
  /** Global ID of the photo */
  photoId: string;
  /** Global ID of the critique session */
  sessionId: string;
  /** Size of stars in pixels */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Displays rating summary with average stars and vote count
 *
 * @example
 * ```tsx
 * <RatingSummary photoId={photoId} sessionId={sessionId} />
 * ```
 */
export function RatingSummary({
  photoId,
  sessionId,
  size = 20,
  className,
}: RatingSummaryProps) {
  const { ratingSummary, loading } = useRatingSummaryForPhoto(photoId, sessionId);

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 animate-pulse', className)}>
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} fill="empty" size={size} fillColor="#d1d5db" />
          ))}
        </div>
        <Text variant="muted" className="text-sm">Loading...</Text>
      </div>
    );
  }

  const averageRating = ratingSummary?.averageRating ?? 0;
  const voteCount = ratingSummary?.voteCount ?? 0;

  // Calculate filled stars based on average
  const getStarFill = (index: number): 'full' | 'half' | 'empty' => {
    const threshold = index + 1;
    if (averageRating >= threshold) return 'full';
    if (averageRating >= threshold - 0.5) return 'half';
    return 'empty';
  };

  if (voteCount === 0) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="flex gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} fill="empty" size={size} fillColor="#d1d5db" emptyColor="transparent" />
          ))}
        </div>
        <Text variant="muted" className="text-sm">No ratings yet</Text>
      </div>
    );
  }

  // Photographer view uses secondary color (teal in dark mode, orange tint in light mode)
  const starColor = 'rgb(var(--color-secondary))';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex gap-0.5" role="img" aria-label={`${averageRating.toFixed(1)} out of 5 stars`}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            fill={getStarFill(i)}
            size={size}
            fillColor={starColor}
            emptyColor="transparent"
          />
        ))}
      </div>
      <Text variant="muted" className="text-sm">
        {averageRating.toFixed(1)} ({voteCount} {voteCount === 1 ? 'vote' : 'votes'})
      </Text>
    </div>
  );
}

RatingSummary.displayName = 'RatingSummary';
